<template>
  <div class="flex flex-col">
    <div class="relative py-2">
      <!-- Floating Label -->
      <label
        v-if="label"
        :for="id"
        :class="[
          'start-3 transition-all duration-50 py-0.5 text-sm flex items-center gap-1',
        ]"
        :dir="props.pageDir"
      >
        <slot name="icon"></slot>
        <span>{{ translate(label || "", props.pageLang || "ar") }}</span>
        <span v-if="required" class="text-red-600">*</span>
      </label>

      <!-- switch Field -->
      <label
        v-if="type === 'switch'"
        class="inline-flex items-center cursor-pointer"
      >
        <input v-model="modelValueRef" type="checkbox" class="sr-only peer" />
        <div
          class="relative w-11 h-6 rounded-full peer bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all border-gray-600 peer-checked:bg-blue-600"
        ></div>
      </label>

      <!-- Textarea Field -->
      <VValidator
        v-else-if="type === 'textarea'"
        :schema="props.schema"
        :dataValidationId="props.dataValidationId"
        :pageLang="props.pageLang"
        :pageDir="props.pageDir"
      >
        <textarea
          :id="id"
          v-model="modelValueRef"
          :refTextarea="refTextarea"
          :disabled="props.disabled"
          :placeholder="translate(placeholder, props.pageLang || 'ar')"
          class="w-full disabled:cursor-not-allowed disabled:bg-gray-100 border h-40 border-secondary-400 shadow-lg rounded-lg px-3 py-2 pt-5 text-sm focus:outline-none focus:ring-1 focus:ring-secondary-800 resize-none"
          :class="classTextarea"
          @focus="isFocused = true"
          @blur="isFocused = false"
        ></textarea>
      </VValidator>

      <!-- Checkbox Field -->
      <label
        v-else-if="type === 'checkbox'"
        class="inline-flex items-center gap-2 cursor-pointer"
      >
        <input
          v-model="modelValueRef"
          type="checkbox"
          class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
          :disabled="props.disabled"
          @change="emits('update:modelValue', modelValueRef)"
        />
        <span>{{ translate(placeholder || "", props.pageLang || "ar") }}</span>
      </label>

      <!-- Date Field -->
      <VValidator
        v-else-if="type === 'date'"
        :schema="props.schema"
        :dataValidationId="props.dataValidationId"
        :pageLang="props.pageLang"
        :pageDir="props.pageDir"
      >
        <input
          :id="id"
          v-model="modelValueRef"
          type="date"
          :disabled="props.disabled"
          class="w-full disabled:cursor-not-allowed disabled:bg-gray-100 border border-secondary-400 shadow-lg rounded-lg px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-secondary-800"
          @focus="isFocused = true"
          @blur="isFocused = false"
        />
      </VValidator>

      <!-- Input Field -->
      <VValidator
        v-else
        :schema="props.schema"
        :dataValidationId="props.dataValidationId"
        :pageLang="props.pageLang"
        :pageDir="props.pageDir"
      >
        <input
          :id="id"
          :ref="refInput"
          v-model="modelValueRef"
          :disabled="props.disabled"
          :type="type"
          :step="props.step"
          :min="props.min"
          :max="props.max"
          :autocomplete="props.autoComplete ? 'true' : 'false'"
          :placeholder="translate(placeholder, props.pageLang || 'ar')"
          class="w-full disabled:cursor-not-allowed disabled:bg-gray-100 border border-secondary-400 shadow-lg rounded-lg px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-secondary-800"
          @focus="isFocused = true"
          @blur="isFocused = false"
        />
      </VValidator>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue"
import { translate } from "src/utils/translation"

// Define Props
const props = defineProps<{
  modelValue: any;
  label?: string;
  id?: string;
  type?: string;
  placeholder?: string | any;
  required?: boolean;
  refInput?: any;
  refTextarea?: any;
  classTextarea?: any;
  schema?: any;
  dataValidationId?: string;
  bgLabel?: string;
  height?: string;
  autoComplete?: boolean;
  pageLang?: string;
  pageDir?: string;
  disabled?: boolean;
  step?: string | number;
  min?: string | number;
  max?: string | number;
}>()

const emits = defineEmits<{
  (e: "update:modelValue", value: any): void;
}>()

const modelValueRef = computed({
  get: () => props.modelValue,
  set: (val) => emits("update:modelValue", val),
})

const isFocused = ref(false)
</script>
